import { useQueryClient } from '@tanstack/react-query';
import { useModeAwareMutation, useModeAwareQuery } from '@/hooks/useModeAwareQuery';
import { mockDocumentsResponse } from '@/lib/demo-data/transaction-documents';
import { useAuth } from '@/providers/AuthProvider';
import { useDocumentApi } from '@/providers/DocumentApiProvider';
import type {
  AdmCreatedDocumentResponse,
  AdmDocumentContext,
  AdmDocumentMetadata,
  AdmFileUploadResponse,
  AdmTagAssociation,
  AdmTagValuePair,
} from '@/types/adm-document';
import { ADM_DOC_TYPE_ID, ADM_FOLDER_ID, ADM_LINK_PRODUCT, ADM_LINK_TYPE, ADM_TAG_NAMES } from '@/types/adm-document';
import type {
  TransactionDocument,
  TransactionDocumentsResponse,
  UploadTransactionDocumentPayload,
} from '@/types/transaction-document';
import { DOCUMENT_UPLOAD_TYPE } from '@/types/transaction-document';
import { uploadTransactionDocumentDemoResponse } from '@/services/demo/transaction-documents-demo.service';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function stripExternalIdPrefix(externalId: string): string {
  return externalId.length > 1 ? externalId.substring(1) : externalId;
}

function mimeTypeFromExtension(ext: string): string {
  const map: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.gif': 'image/gif',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.csv': 'text/csv',
    '.txt': 'text/plain',
  };
  return map[ext.toLowerCase()] ?? 'application/octet-stream';
}

export function mapAdmDocumentToTransactionDocument(
  doc: AdmDocumentMetadata,
  transactionReference: string,
): TransactionDocument {
  const ext = doc.DocFileExtension ?? '';
  return {
    id: String(doc.Id),
    fileName: `${doc.DocTitle}${ext}`,
    fileType: mimeTypeFromExtension(ext),
    fileSize: 0,
    documentType: doc.DocumentType,
    uploadedAt: doc.DocDatePutin,
    uploadedByEmail: doc.UserName,
    transactionReference,
  };
}

export async function getAdmAuthHeaders(
  getAccessToken?: () => Promise<string | null>,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (getAccessToken) {
    const token = await getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return headers;
}

/**
 * ADM builds its indexing SQL from every tag associated with the document type, so a
 * `TagValues` list that omits any of them fails with
 * `Must declare the scalar variable "@TAG_<id>"`. Tag ids are also configured per ADM
 * instance, so both the ids and the full tag set are read from the document type.
 */
const tagAssociationCache = new Map<string, AdmTagAssociation[]>();

export async function fetchDocTypeTagAssociations(
  ctx: AdmDocumentContext,
  headers: Record<string, string>,
  docTypeId: number = ADM_DOC_TYPE_ID,
): Promise<AdmTagAssociation[]> {
  const cacheKey = `${ctx.instanceUrl}|${docTypeId}`;
  const cached = tagAssociationCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const url = `${ctx.instanceUrl}/DocumentTypes/${docTypeId}/TagAssociations`;
  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    console.error('[ADM] TagAssociations failed', response.status, response.statusText);
    throw new Error(`ADM TagAssociations failed (HTTP ${response.status})`);
  }

  const associations: AdmTagAssociation[] = await response.json();
  tagAssociationCache.set(cacheKey, associations);
  console.log(
    '[ADM] TagAssociations for docType',
    docTypeId,
    ':',
    associations.map((x) => [x.Tag.Id, x.Tag.Name]),
  );
  return associations;
}

function normaliseTagName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Documents are listed back via an `X-DocumentTags` filter on these two tags, so a document
 * created without them would not appear in the Documents tab.
 */
const REQUIRED_TAG_NAMES: string[] = [ADM_TAG_NAMES.DATABASE_NAME, ADM_TAG_NAMES.SALES_LEDGER_PRIMARY];

/**
 * Every tag the document type exposes gets an entry — tags we have no value for are sent
 * empty rather than omitted, because ADM expects a value for each associated tag.
 */
export function buildTagValues(
  ctx: AdmDocumentContext,
  payload: UploadTransactionDocumentPayload,
  associations: AdmTagAssociation[],
): AdmTagValuePair[] {
  const valuesByTagName = new Map<string, string | undefined>([
    [normaliseTagName(ADM_TAG_NAMES.DATABASE_NAME), ctx.dbName],
    [
      normaliseTagName(ADM_TAG_NAMES.SALES_LEDGER_PRIMARY),
      payload.externalId ? stripExternalIdPrefix(payload.externalId) : undefined,
    ],
    [normaliseTagName(ADM_TAG_NAMES.ACCOUNT_CODE), payload.accountCode],
    [normaliseTagName(ADM_TAG_NAMES.ACCOUNT_NAME), payload.customerName],
    [normaliseTagName(ADM_TAG_NAMES.HEADER_REF), payload.headerReference],
    [normaliseTagName(ADM_TAG_NAMES.TRANSACTION_DATE), payload.transactionDate],
    [normaliseTagName(ADM_TAG_NAMES.DESCRIPTION), payload.description],
    [normaliseTagName(ADM_TAG_NAMES.TRANSACTION_TYPE), payload.transactionType],
  ]);

  for (const requiredName of REQUIRED_TAG_NAMES) {
    const normalised = normaliseTagName(requiredName);
    if (!associations.some((x) => normaliseTagName(x.Tag.Name) === normalised)) {
      throw new Error(`ADM document type ${ADM_DOC_TYPE_ID} has no "${requiredName}" tag — cannot index the document`);
    }
  }

  return associations.map((association) => ({
    TagId: association.Tag.Id,
    TagValue: valuesByTagName.get(normaliseTagName(association.Tag.Name)) ?? '',
  }));
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch all documents linked to a transaction via ADM DocumentMetadata endpoint.
 * Uses X-DocumentTags header to filter by ExternalId (Sales Ledger Primary).
 */
export function useTransactionDocuments(transactionReference: string | undefined, externalId?: string) {
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareQuery(
    ['transactionDocuments', transactionReference ?? ''],
    async () => {
      console.log('[ADM] useTransactionDocuments — txRef:', transactionReference, 'externalId:', externalId);
      if (!externalId) {
        console.log('[ADM] useTransactionDocuments — no externalId, returning empty');
        return { documents: [], totalCount: 0 } as TransactionDocumentsResponse;
      }

      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error('ADM not initialized — could not discover instance');
      }

      const headers = await getAdmAuthHeaders(getAccessToken);

      const strippedId = stripExternalIdPrefix(externalId);
      console.log(
        '[ADM] useTransactionDocuments — strippedExternalId:',
        strippedId,
        'dbName:',
        ctx.dbName,
        'instanceUrl:',
        ctx.instanceUrl,
      );
      const tagFilters = JSON.stringify({
        [ADM_TAG_NAMES.SALES_LEDGER_PRIMARY]: strippedId,
        [ADM_TAG_NAMES.DATABASE_NAME]: ctx.dbName,
      });
      console.log('[ADM] useTransactionDocuments — X-DocumentTags:', tagFilters);
      headers['X-DocumentTags'] = tagFilters;

      const params = new URLSearchParams({
        dbName: ctx.dbName,
        linkType: ADM_LINK_TYPE,
        linkProduct: ADM_LINK_PRODUCT,
      });

      const url = `${ctx.instanceUrl}/DocumentMetadata?${params.toString()}`;
      console.log('[ADM] useTransactionDocuments — fetching:', url);
      const response = await fetch(url, { method: 'GET', headers });

      if (!response.ok) {
        console.error('[ADM] useTransactionDocuments — DocumentMetadata failed', response.status, response.statusText);
        throw new Error(`ADM DocumentMetadata failed (HTTP ${response.status})`);
      }

      const admDocs: AdmDocumentMetadata[] = await response.json();
      console.log('[ADM] useTransactionDocuments — received', admDocs.length, 'documents');

      const documents = admDocs.map((d) => mapAdmDocumentToTransactionDocument(d, transactionReference ?? ''));

      return { documents, totalCount: documents.length } as TransactionDocumentsResponse;
    },
    mockDocumentsResponse,
    {
      enabled: !!transactionReference,
      staleTime: 60 * 1000,
    },
  );
}

/**
 * Upload a document to ADM: FileUpload then CreateDocument (two-step).
 * Tags are populated from the transaction data passed in the payload.
 */
export function useUploadTransactionDocument() {
  const queryClient = useQueryClient();
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareMutation<TransactionDocument, Error, UploadTransactionDocumentPayload>(
    async (payload) => {
      console.log(
        '[ADM] useUploadTransactionDocument — file:',
        payload.file.name,
        'txRef:',
        payload.transactionReference,
        'externalId:',
        payload.externalId,
      );
      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error('ADM not initialized — could not discover instance');
      }

      // Resolve tags first: a failure here must not leave an orphaned temp file in ADM.
      const associations = await fetchDocTypeTagAssociations(ctx, await getAdmAuthHeaders(getAccessToken));
      const tagValues = buildTagValues(ctx, payload, associations);
      console.log('[ADM] Upload — tags:', tagValues);

      // Step 1: Upload the file
      console.log('[ADM] Upload Step 1 — FileUpload to', ctx.instanceUrl);
      const uploadHeaders = await getAdmAuthHeaders(getAccessToken);
      delete uploadHeaders['Content-Type'];

      const formData = new FormData();
      formData.append('file', payload.file);

      const uploadResponse = await fetch(`${ctx.instanceUrl}/FileUpload`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData,
      });

      if (!uploadResponse.ok) {
        console.error('[ADM] FileUpload failed', uploadResponse.status, uploadResponse.statusText);
        throw new Error(`ADM FileUpload failed (HTTP ${uploadResponse.status})`);
      }

      const uploadJson: AdmFileUploadResponse = await uploadResponse.json();
      const identifiers = uploadJson.Identifiers ?? [];
      if (identifiers.length === 0) {
        throw new Error('ADM FileUpload returned no entries');
      }

      const serverFileKey = identifiers[0].ServerFileKey;
      console.log('[ADM] Upload Step 1 complete — serverFileKey:', serverFileKey);

      // Step 2: Create the document with tags
      console.log('[ADM] Upload Step 2 — CreateDocument');
      const createHeaders = await getAdmAuthHeaders(getAccessToken);

      const fileName = payload.file.name;
      const titleWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;

      const createBody = {
        ServerFileKey: serverFileKey,
        Attributes: {
          DestinationFolderId: ADM_FOLDER_ID,
          Title: titleWithoutExt,
        },
        IndexingInfo: {
          DocTypeId: ADM_DOC_TYPE_ID,
          TagValues: tagValues,
        },
      };

      const createResponse = await fetch(`${ctx.instanceUrl}/Documents`, {
        method: 'POST',
        headers: createHeaders,
        body: JSON.stringify(createBody),
      });

      if (!createResponse.ok) {
        const detail = await createResponse.text().catch(() => '');
        console.error('[ADM] CreateDocument failed', createResponse.status, createResponse.statusText, detail);
        throw new Error(`ADM CreateDocument failed (HTTP ${createResponse.status}) ${detail.slice(0, 300)}`.trim());
      }

      const created: AdmCreatedDocumentResponse = await createResponse.json();
      console.log('[ADM] Upload Step 2 complete — created document ID:', created.Id);

      return {
        id: String(created.Id),
        fileName,
        fileType: payload.file.type,
        fileSize: payload.file.size,
        documentType: DOCUMENT_UPLOAD_TYPE,
        uploadedAt: new Date().toISOString(),
        uploadedByEmail: '',
        transactionReference: payload.transactionReference,
      };
    },
    uploadTransactionDocumentDemoResponse,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['transactionDocuments', variables.transactionReference],
        });
      },
    },
  );
}

/**
 * Delete a document from ADM by its numeric ID.
 */
export function useDeleteTransactionDocument() {
  const queryClient = useQueryClient();
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareMutation<void, Error, { documentId: string; transactionReference: string }>(
    async ({ documentId }) => {
      console.log('[ADM] useDeleteTransactionDocument — documentId:', documentId);
      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error('ADM not initialized — could not discover instance');
      }

      const headers = await getAdmAuthHeaders(getAccessToken);
      const response = await fetch(`${ctx.instanceUrl}/Documents/${documentId}`, { method: 'DELETE', headers });

      if (!response.ok) {
        console.error('[ADM] Delete failed', response.status, response.statusText);
        throw new Error(`ADM Delete failed (HTTP ${response.status})`);
      }
      console.log('[ADM] Delete successful — documentId:', documentId);
    },
    () => undefined,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['transactionDocuments', variables.transactionReference],
        });
      },
    },
  );
}

/**
 * Download a document from ADM as a Blob and trigger a browser download.
 */
export function useDownloadTransactionDocument() {
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareMutation<void, Error, { documentId: string; fileName: string }>(
    async ({ documentId, fileName }) => {
      console.log('[ADM] useDownloadTransactionDocument — documentId:', documentId, 'fileName:', fileName);
      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error('ADM not initialized — could not discover instance');
      }

      const headers = await getAdmAuthHeaders(getAccessToken);
      delete headers['Content-Type'];

      const response = await fetch(`${ctx.instanceUrl}/Documents/${documentId}/Download`, { method: 'GET', headers });

      if (!response.ok) {
        console.error('[ADM] Download failed', response.status, response.statusText);
        throw new Error(`ADM Download failed (HTTP ${response.status})`);
      }

      const blob = await response.blob();
      console.log('[ADM] Download successful — blob size:', blob.size);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    () => undefined,
  );
}

/**
 * Preview a document from ADM — opens in a new browser tab.
 */
export function usePreviewTransactionDocument() {
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();

  return useModeAwareMutation<void, Error, { documentId: string }>(
    async ({ documentId }) => {
      console.log('[ADM] usePreviewTransactionDocument — documentId:', documentId);
      const ctx = await getDocumentContext();
      if (!ctx) {
        throw new Error('ADM not initialized — could not discover instance');
      }

      const headers = await getAdmAuthHeaders(getAccessToken);
      delete headers['Content-Type'];

      const response = await fetch(`${ctx.instanceUrl}/Documents/${documentId}/preview`, { method: 'GET', headers });

      if (!response.ok) {
        console.error('[ADM] Preview failed', response.status, response.statusText);
        throw new Error(`ADM Preview failed (HTTP ${response.status})`);
      }

      const blob = await response.blob();
      console.log('[ADM] Preview successful — blob size:', blob.size);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    () => undefined,
  );
}

// ---------------------------------------------------------------------------
// Imperative fetcher (not a hook — safe to call inside callbacks)
// ---------------------------------------------------------------------------

export async function fetchAdmDocumentsForTransaction(
  ctx: AdmDocumentContext,
  getAccessToken: () => Promise<string | null>,
  externalId: string,
  limit = 10,
): Promise<TransactionDocument[]> {
  const strippedId = stripExternalIdPrefix(externalId);

  const headers = await getAdmAuthHeaders(getAccessToken);
  const tagFilters = JSON.stringify({
    [ADM_TAG_NAMES.SALES_LEDGER_PRIMARY]: strippedId,
    [ADM_TAG_NAMES.DATABASE_NAME]: ctx.dbName,
  });
  headers['X-DocumentTags'] = tagFilters;

  const params = new URLSearchParams({
    dbName: ctx.dbName,
    linkType: ADM_LINK_TYPE,
    linkProduct: ADM_LINK_PRODUCT,
  });

  const url = `${ctx.instanceUrl}/DocumentMetadata?${params.toString()}`;
  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    throw new Error(`ADM DocumentMetadata failed (HTTP ${response.status})`);
  }

  const admDocs: AdmDocumentMetadata[] = await response.json();
  return admDocs.map((d) => mapAdmDocumentToTransactionDocument(d, externalId)).slice(0, limit);
}
